import React, { useState } from 'react';
import { string, func, bool } from 'prop-types';
import classNames from 'classnames';
import { useConfiguration } from '../../context/configurationContext';
import { intlShape, injectIntl } from '../../util/reactIntl';
import { lazyLoadWithDimensions } from '../../util/uiHelpers';
import { propTypes } from '../../util/types';
import { formatMoney } from '../../util/currency';
import { ensureListing, ensureUser } from '../../util/data';
import { richText } from '../../util/richText';
import { createSlug } from '../../util/urlHelpers';

import { AspectRatioWrapper, ResponsiveImage } from '../../components';

import css from './CartPanel.module.css';
import moment from 'moment';
import IconCard from '../SavedCardDetails/IconCard/IconCard';

const MIN_LENGTH_FOR_LONG_WORDS = 10;
const LazyImage = lazyLoadWithDimensions(ResponsiveImage, { loadAfterInitialRendering: 3000 });

/** Helpers to pick per-day price from pricePackages (packages prices are assumed to be in currency units) */
const parsePackages = packagesRaw => {
  if (!Array.isArray(packagesRaw)) return [];
  return packagesRaw
    .map(p => {
      const min = p?.minDays !== undefined && p.minDays !== '' ? parseInt(p.minDays, 10) : null;
      const max = p?.maxDays !== undefined && p.maxDays !== '' ? parseInt(p.maxDays, 10) : null;
      const price = p?.price !== undefined && p.price !== '' ? parseFloat(p.price) : null;
      return { min, max, price, raw: p };
    })
    .filter(p => Number.isFinite(p.price))
    .sort((a, b) => {
      const am = a.min === null ? Number.POSITIVE_INFINITY : a.min;
      const bm = b.min === null ? Number.POSITIVE_INFINITY : b.min;
      return am - bm;
    });
};

/**
 * Given a listing item and days, return per-day price in currency units (not cents).
 * Behavior:
 * - If pricePackages exist, try to find a package where min <= days <= max (null is open-ended).
 * - If days > highest max, use last package's price.
 * - If no packages or no match, fallback to listing.attributes.price.amount (converted from cents to units).
 */
const getPerDayPriceFromPackages = (item, days) => {
  const packages = parsePackages(item?.attributes?.publicData?.pricePackages || []);
  // fallback: listing base price in units (convert cents to units)
  const listingPriceUnits = Number(item?.attributes?.price?.amount || 0) / 100;

  if (packages.length === 0) {
    return listingPriceUnits;
  }

  // find package where min <= days <= max (null treated as open-ended)
  const matched = packages.find(p => {
    const minOk = p.min === null ? true : days >= p.min;
    const maxOk = p.max === null ? true : days <= p.max;
    return minOk && maxOk;
  });

  if (matched) return matched.price;

  // if days > highest defined max => use last package's price
  const packagesWithMax = packages.filter(p => Number.isFinite(p.max));
  if (packagesWithMax.length > 0) {
    const highestMax = Math.max(...packagesWithMax.map(p => p.max));
    const lastPackage = packages[packages.length - 1];
    if (days > highestMax) return lastPackage.price;
  }

  // days < all minDays or no match -> fallback to listing priceUnits
  return listingPriceUnits;
};

export const CartPanelComponent = props => {
  const config = useConfiguration();
  const {
    className,
    rootClassName,
    intl,
    listing,
    renderSizes,
    setActiveListing,
    showAuthorInfo,
    bookmarks,
    handleWishlist,
    isAuthenticated,
    handleRemoveFromState = () => {},
    currentUser,
    checkAvailabilty,
    onQuantityChange = () => {},
  } = props;

  const classes = classNames(rootClassName || css.root, className);
  const currentListing = ensureListing(listing);

  const { title = '', price } = currentListing.attributes || {};
  const { seats } = currentListing || {};

  const id = currentListing.id.uuid;

  // Dates and diff calculation
  const startMoment = moment(currentListing?.startDate);
  const endMoment = moment(currentListing?.endDate);

  // diffInDays: ensure at least 1 day
  let diffInDays = 1;
  if (startMoment.isValid() && endMoment.isValid() && endMoment.isAfter(startMoment)) {
    diffInDays = endMoment.diff(startMoment, 'days');
    if (diffInDays < 1) diffInDays = 1;
  }

  // Per-day price in currency units (e.g., 200)
  const perDayPriceUnits = getPerDayPriceFromPackages(currentListing, diffInDays);

  // Total price units = per-day * days * seats
  const totalUnits = perDayPriceUnits * diffInDays * Number(seats || 1);

  // For formatMoney we must supply a Money-shaped object with integer amount (cents) and currency
  const currency = price?.currency || config.currency || 'USD';

  const perDayAmountCents = Math.round(perDayPriceUnits * 100);
  const totalAmountCents = Math.round(totalUnits * 100);

  const perDayMoney = {
    _sdkType: 'Money',
    amount: perDayAmountCents,
    currency,
  };

  const totalMoney = {
    _sdkType: 'Money',
    amount: totalAmountCents,
    currency,
  };

  // First image logic
  const slug = createSlug(title);
  const author = ensureUser(listing.author);
  const authorName = author.attributes?.profile?.displayName;
  let firstImage =
    currentListing.images && currentListing.images.length > 0 ? currentListing.images[0] : null;

  const { aspectWidth = 1, aspectHeight = 1, variantPrefix = 'scaled-small' } =
    config.layout.listingImage || {};
  const variants = firstImage
    ? Object.keys(firstImage?.attributes?.variants || {}).filter(k => k.startsWith('scaled-small'))
    : [];

  const [isHovered, setHovered] = useState('');
  const setActivePropsMaybe = setActiveListing
    ? {
        onMouseEnter: () => setActiveListing(currentListing.id),
        onMouseLeave: () => setActiveListing(null),
      }
    : null;

  if (isHovered && currentListing && currentListing.images && currentListing.images.length > 1) {
    firstImage = currentListing.images[1];
  }

  const startDate = moment(currentListing?.startDate).format('D MMM YYYY');
  const endDate = moment(currentListing?.endDate)
    .subtract(1, 'days')
    .format('D MMM YYYY');

  return (
    <div className={css.root}>
      <div className={css.avatarWrapper}>
        <AspectRatioWrapper
          className={css.aspectRatioWrapper}
          width={aspectWidth}
          height={aspectHeight}
          {...setActivePropsMaybe}
          onMouseOver={() => setHovered('hover')}
          onMouseOut={() => setHovered('')}
        >
          <LazyImage
            rootClassName={css.rootForImage}
            alt={title}
            image={firstImage}
            variants={variants}
            sizes={renderSizes}
          />
        </AspectRatioWrapper>

        <div className={css.info}>
          <div className={css.mainInfo}>
            <div className={css.title}>
              {richText(title, {
                longWordMinLength: MIN_LENGTH_FOR_LONG_WORDS,
                longWordClass: css.longWord,
              })}
            </div>

            <div className={css.cartValue}>
              {diffInDays} {diffInDays === 1 ? 'day' : 'days'} × {seats || 1}{' '}
              {seats <= 1 ? 'seat' : 'seats'}
            </div>

            <div className={css.priceBreakdown}>
              {/* <div className={css.total}>
                Total: <span className={css.totalValue}>{formatMoney(intl, totalMoney)}</span>
              </div> */}
            </div>
          </div>
        </div>
      </div>

      <div className={css.dateWrapper}>
        <div className={css.label}>Rental Period: </div>
        <div className={css.dateValue}>
          {startDate} to {endDate}
        </div>
      </div>

      <div className={css.rightPanel}>
        <div>
          <span className={css.totalPrice}>€{totalUnits}</span>
        </div>
        <div
          className={css.deleteBtn}
          onClick={e => {
            handleRemoveFromState(id);
            handleWishlist(id, e, null);
          }}
        >
          <IconCard brand="trash" />
          {bookmarks && bookmarks.findIndex(e => e.id == id) > -1 ? 'Delete' : null}
        </div>
      </div>
    </div>
  );
};

CartPanelComponent.defaultProps = {
  className: null,
  rootClassName: null,
  renderSizes: null,
  setActiveListing: null,
  showAuthorInfo: true,
};

CartPanelComponent.propTypes = {
  className: string,
  rootClassName: string,
  intl: intlShape.isRequired,
  listing: propTypes.listing.isRequired,
  showAuthorInfo: bool,

  // Responsive image sizes hint
  renderSizes: string,

  setActiveListing: func,
};

export default injectIntl(CartPanelComponent);
